import {
  AlertCircle,
  Building2,
  Check,
  ChevronsUpDown,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import * as React from 'react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useTenant } from '@/features/tenants'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { TenantError } from '@/shared/lib/errors/auth-errors'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'

interface TenantSwitcherProps {
  className?: string
}

export function TenantSwitcher({ className }: TenantSwitcherProps) {
  // Get auth state for user and authentication status
  const { user, isAuthenticated } = useAuthStore()

  // Get tenant state from TenantProvider
  const {
    currentTenant,
    tenants: availableTenants,
    isLoadingTenants: tenantsLoading,
    isSwitchingTenant,
    switchTenant,
    refreshTenants,
  } = useTenant()

  const [isOpen, setIsOpen] = React.useState(false)
  const [lastError, setLastError] = React.useState<string | null>(null)

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null
  }

  const handleTenantSwitch = async (tenantId: string) => {
    if (tenantId === currentTenant?.id) {
      return
    }

    setLastError(null)

    try {
      await switchTenant(tenantId)
      setIsOpen(false)
      // Tenant context will update automatically without page reload
    } catch (error) {
      const errorMessage =
        error instanceof TenantError
          ? error.message
          : 'Failed to switch organization. Please try again.'

      setLastError(errorMessage)
      toast.error(extractErrorMessage(error))

      // Don't close the dropdown if there's an error so user can retry
    }
  }

  const handleRetry = async () => {
    setLastError(null)
    try {
      await refreshTenants()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const getTenantDisplayName = (tenantName: string) => {
    return tenantName || 'Unnamed Organization'
  }

  const _getTenantRoleBadge = (role: string) => {
    const roleColors = {
      admin: 'text-emerald-600 bg-emerald-50 border-emerald-200',
      member: 'text-blue-600 bg-blue-50 border-blue-200',
      viewer: 'text-gray-600 bg-gray-50 border-gray-200',
    }

    return roleColors[role as keyof typeof roleColors] || roleColors.member
  }

  const generateOrgNameFromEmail = (email?: string): string => {
    if (!email) return 'Your Organization'
    const username = email.split('@')[0]
    if (username.includes('.')) {
      return (
        username
          .split('.')
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ') + "'s Organization"
      )
    }
    return (
      username.charAt(0).toUpperCase() + username.slice(1) + "'s Organization"
    )
  }

  // Helper to check if organization name is a default/system name
  const isDefaultOrganizationName = (name?: string): boolean => {
    if (!name) return true
    const lowerName = name.toLowerCase()
    return (
      name.includes('@') ||
      lowerName.includes('tenant') ||
      name.match(/^[a-z]+-[a-z]+-\d+$/) !== null || // Pattern like "test-tenant-123"
      !!(currentTenant && name === currentTenant.slug)
    )
  }

  // Determine the display name, with better persistence across refresh
  const getDisplayName = (): string => {
    // Priority 1: Use currentTenant for organization name (if it's a proper name)
    if (currentTenant) {
      const orgName = currentTenant.name
      if (orgName && !isDefaultOrganizationName(orgName)) {
        return getTenantDisplayName(orgName)
      }
      // If it's a default name, show email-based fallback instead
      return generateOrgNameFromEmail(user?.email)
    }

    // Priority 2: Try to get tenant from localStorage if currentTenant is loading
    const savedTenantId = localStorage.getItem('current_tenant_id')
    if (savedTenantId && availableTenants && availableTenants.length > 0) {
      const savedTenant = availableTenants.find(t => t.id === savedTenantId)
      if (savedTenant?.name && !isDefaultOrganizationName(savedTenant.name)) {
        return getTenantDisplayName(savedTenant.name)
      }
      // If it's a default name, show email-based fallback instead
      return generateOrgNameFromEmail(user?.email)
    }

    // Priority 3: If still loading, show first available tenant if any (if it's a proper name)
    if (tenantsLoading && availableTenants && availableTenants.length > 0) {
      const firstTenant = availableTenants[0].name
      if (firstTenant && !isDefaultOrganizationName(firstTenant)) {
        return getTenantDisplayName(firstTenant)
      }
      // If it's a default name, show email-based fallback instead
      return generateOrgNameFromEmail(user?.email)
    }

    return tenantsLoading ? 'Loading...' : generateOrgNameFromEmail(user?.email)
  }

  // Check if we should show as dropdown or just text
  // Only show dropdown if we have multiple tenants loaded
  const shouldShowDropdown = availableTenants && availableTenants.length > 1

  // If we have exactly one tenant OR no tenants loaded (fallback case), show as plain text/button without dropdown
  if (!shouldShowDropdown) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 font-normal',
          className
        )}
      >
        <Building2 className='size-4 shrink-0' />
        <div className='flex flex-col items-start min-w-0'>
          <span className='truncate'>{getDisplayName()}</span>
          {/* Role removed from single-tenant view */}
        </div>
      </div>
    )
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={isOpen}
          className={cn('w-[280px] justify-between font-normal', className)}
          disabled={tenantsLoading || isSwitchingTenant}
        >
          <div className='flex items-center gap-2 min-w-0'>
            <Building2 className='size-4 shrink-0' />
            <div className='flex flex-col items-start min-w-0'>
              <span className='truncate'>{getDisplayName()}</span>
              {/* Role removed from dropdown trigger */}
            </div>
          </div>
          {tenantsLoading || isSwitchingTenant ? (
            <Loader2 className='size-4 animate-spin shrink-0' />
          ) : (
            <ChevronsUpDown className='size-4 shrink-0 opacity-50' />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-[280px]' align='start'>
        <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {!availableTenants || availableTenants.length === 0 ? (
          <div className='p-3'>
            {lastError ? (
              <div className='flex flex-col gap-2'>
                <div className='flex items-center gap-2 text-sm text-destructive'>
                  <AlertCircle className='size-4 shrink-0' />
                  <span className='text-wrap'>{lastError}</span>
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleRetry}
                  disabled={tenantsLoading}
                  className='gap-2'
                >
                  <RefreshCw
                    className={cn('size-4', tenantsLoading && 'animate-spin')}
                  />
                  Try Again
                </Button>
              </div>
            ) : (
              <div className='text-sm text-muted-foreground text-center'>
                No organizations available
              </div>
            )}
          </div>
        ) : (
          availableTenants.map(tenant => (
            <DropdownMenuItem
              key={tenant.id}
              className='flex items-center justify-between p-2 cursor-pointer'
              onClick={() => handleTenantSwitch(tenant.id)}
              disabled={isSwitchingTenant}
            >
              <div className='flex items-center gap-2 min-w-0 flex-1'>
                <Building2 className='size-4 shrink-0' />
                <div className='flex flex-col min-w-0 flex-1'>
                  <span className='truncate font-medium'>
                    {getTenantDisplayName(tenant.name)}
                  </span>
                  <div className='flex items-center gap-2'>
                    <span className='text-xs text-muted-foreground'>
                      {tenant.slug}
                    </span>
                  </div>
                </div>
              </div>
              {currentTenant?.id === tenant.id && (
                <Check className='size-4 shrink-0' />
              )}
            </DropdownMenuItem>
          ))
        )}

        {lastError && availableTenants && availableTenants.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className='p-2 text-xs text-destructive flex items-center gap-2'>
              <AlertCircle className='size-3 shrink-0' />
              <span>{lastError}</span>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
